import type { QueryClient } from "@tanstack/react-query";

import { randomUUID } from "expo-crypto";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type {
  Item as ApiItem,
  ItemAttachment as ApiItemAttachment,
  ItemInput,
  ItemReminder as ApiItemReminder,
  Kit as ApiKit,
} from "~/api/generated/types.gen";

/** The rows are the API's wire format (snake_case, the old Supabase row shape). */
export type Kit = ApiKit;
export type Item = ApiItem;
export type ItemAttachment = ApiItemAttachment;
export type ItemReminder = ApiItemReminder;

/** Client-generated ids so an optimistic row keeps its identity after the write. */
export const generateId = () => randomUUID();

/**
 * A visitor with no account is a guest: their gear lives only in this scope of
 * the (persisted) query cache, on the device, until they create an account and
 * `migrateGuestToRemote` copies it over. A signed-in user's rows use their id.
 */
export const GUEST = "guest" as const;

/** Query keys — scoped to the signed-in user, or to the on-device guest. */
export const gearKeys = {
  kits: (scope: string) => ["kits", scope] as const,
  items: (scope: string) => ["items", scope] as const,
  attachments: (scope: string) => ["item-attachments", scope] as const,
  reminders: (scope: string) => ["item-reminders", scope] as const,
};

export const DEFAULT_KIT_NAME = "Workshop";

/** The starter kit every account gets from the API; guests get it locally. */
export function defaultGuestKit(): Kit {
  const now = new Date().toISOString();
  return {
    id: randomUUID(),
    owner_id: GUEST,
    name: DEFAULT_KIT_NAME,
    notes: null,
    color: "#3D5A80",
    deleted: false,
    created_at: now,
    updated_at: now,
  };
}

export async function fetchKits(): Promise<Kit[]> {
  const { items } = await unwrap(api.listKits());
  return items.slice().sort((a, b) => a.name.localeCompare(b.name));
}

export async function fetchItems(): Promise<Item[]> {
  const { items } = await unwrap(api.listItems());
  return items.slice().sort((a, b) => a.name.localeCompare(b.name));
}

export async function fetchItemAttachments(): Promise<ItemAttachment[]> {
  const { items } = await unwrap(api.listItemAttachments());
  return items.slice().sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export async function fetchItemReminders(): Promise<ItemReminder[]> {
  const { items } = await unwrap(api.listItemReminders());
  return items.slice().sort((a, b) => a.due_on.localeCompare(b.due_on));
}

/** The writable fields of an item row (what `PUT /v1/items/{id}` accepts). */
export function toItemInput(item: Item, kitId: string | null): ItemInput {
  return {
    kit_id: kitId,
    name: item.name,
    category: item.category,
    brand: item.brand,
    model: item.model,
    serial_number: item.serial_number,
    specs: item.specs,
    purchase_date: item.purchase_date,
    purchase_price: item.purchase_price,
    currency: item.currency,
    vendor: item.vendor,
    warranty_expires_at: item.warranty_expires_at,
    service_interval_months: item.service_interval_months,
    notes: item.notes,
    status: item.status,
  };
}

/**
 * Copies a guest's on-device gear into the account that was just created or
 * signed in to, then forgets the local copy. Safe to retry: rows are upserted
 * by their client-generated id. The guest's starter kit maps onto the account's
 * own kit of the same name (the API provisions one at sign-up) instead of
 * creating a duplicate.
 */
export async function migrateGuestToRemote(
  queryClient: QueryClient,
  userId: string,
) {
  const kits =
    queryClient
      .getQueryData<Kit[]>(gearKeys.kits(GUEST))
      ?.filter((kit) => !kit.deleted) ?? [];
  const items =
    queryClient
      .getQueryData<Item[]>(gearKeys.items(GUEST))
      ?.filter((item) => !item.deleted) ?? [];
  const reminders =
    queryClient.getQueryData<ItemReminder[]>(gearKeys.reminders(GUEST)) ?? [];

  const onlyStarterKit =
    items.length === 0 &&
    kits.every((kit) => kit.name === DEFAULT_KIT_NAME) &&
    kits.length <= 1;
  if (!onlyStarterKit) {
    const remoteKits = await fetchKits();
    const kitIds = new Map<string, string>();
    for (const kit of kits) {
      const existing = remoteKits.find((remote) => remote.name === kit.name);
      if (existing) {
        kitIds.set(kit.id, existing.id);
        continue;
      }
      kitIds.set(kit.id, kit.id);
      await unwrap(
        api.putKit({
          path: { id: kit.id },
          body: { name: kit.name, notes: kit.notes, color: kit.color },
        }),
      );
    }
    for (const item of items) {
      await unwrap(
        api.putItem({
          path: { id: item.id },
          body: toItemInput(
            item,
            item.kit_id ? (kitIds.get(item.kit_id) ?? null) : null,
          ),
        }),
      );
    }
    const itemIds = new Set(items.map((item) => item.id));
    for (const reminder of reminders) {
      if (reminder.deleted || !itemIds.has(reminder.item_id)) continue;
      await unwrap(
        api.putItemReminder({
          path: { id: reminder.id },
          body: {
            item_id: reminder.item_id,
            kind: reminder.kind,
            due_on: reminder.due_on,
            notes: reminder.notes,
          },
        }),
      );
    }
  }

  for (const key of [
    gearKeys.kits(GUEST),
    gearKeys.items(GUEST),
    gearKeys.attachments(GUEST),
    gearKeys.reminders(GUEST),
  ]) {
    queryClient.removeQueries({ queryKey: key });
  }
  for (const key of [
    gearKeys.kits(userId),
    gearKeys.items(userId),
    gearKeys.reminders(userId),
  ]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}
